import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import PaxInput from "../components/PaxInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full h-12 px-4 rounded-lg border text-sm bg-white focus:outline-none focus:ring-2";

export default function EventLanding() {
  const { rid, slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "",
    party_size: 2, time: "", notes: "", consent: false, website: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/event/${slug}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.event.landing_titolo} — ${r.data.brand.name}`;
    }).catch(() => setError(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/event/${slug}/book`, form);
      setDone(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || "Errore durante la prenotazione. Riprova.");
    } finally { setBusy(false); }
  };

  if (error) return <Centered msg="Evento non disponibile o prenotazioni chiuse." testid="landing-error" />;
  if (!data) return <Centered msg="Caricamento..." testid="landing-loading" />;

  const { event: ev, brand, promotion, coupon_name } = data;
  const c = brand.colors;
  const dataLabel = new Date(ev.data + "T12:00:00").toLocaleDateString("it-IT",
    { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="event-landing">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
          {brand.assets.logo
            ? <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.assets.logo}`} alt={brand.name} className="h-10 w-auto object-contain" data-testid="landing-logo" />
            : <span className="font-extrabold text-lg" style={{ color: c.primary }}>{brand.name}</span>}
        </div>
      </header>

      {brand.assets.cover && (
        <img src={`${process.env.REACT_APP_BACKEND_URL}${brand.assets.cover}`} alt=""
          className="w-full h-44 sm:h-56 object-cover" data-testid="landing-cover" />
      )}

      <main className="max-w-xl mx-auto px-4 py-6 space-y-5">
        {done ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center space-y-3" data-testid="booking-success">
            <div className="text-4xl">✓</div>
            <h2 className="font-extrabold text-xl" style={{ color: c.primary }}>Richiesta inviata!</h2>
            <p className="text-sm text-zinc-600">
              {dataLabel} · ore {done.reservation.time} · {done.reservation.party_size} persone.
              Riceverai la conferma dal ristorante.
            </p>
            {done.coupon_code && (
              <div className="rounded-lg p-4 border-2 border-dashed" style={{ borderColor: c.accent }} data-testid="booking-coupon">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">Il tuo coupon</div>
                <div className="font-mono font-black text-2xl" style={{ color: c.accent }}>{done.coupon_code}</div>
                <div className="text-xs text-zinc-500">{coupon_name}</div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div>
              <h1 className="font-extrabold text-2xl sm:text-3xl tracking-tight" style={{ color: c.primary }} data-testid="landing-title">
                {ev.landing_titolo}
              </h1>
              {ev.landing_testo && <p className="text-sm text-zinc-600 mt-2" data-testid="landing-text">{ev.landing_testo}</p>}
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-4 space-y-1.5 text-sm" data-testid="landing-details">
              <div><span className="font-bold">Data:</span> {dataLabel} <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 ml-1">DATA FISSA</span></div>
              <div><span className="font-bold">Orario:</span> {ev.ora_da} – {ev.ora_a}</div>
              {ev.caparra_attiva && <div><span className="font-bold">Caparra:</span> €{ev.caparra_importo} richiesta alla conferma</div>}
              {ev.carta_garanzia && <div><span className="font-bold">Garanzia:</span> carta di credito richiesta alla conferma</div>}
            </div>

            {promotion && (
              <div className="rounded-xl p-4 text-white" style={{ backgroundColor: c.primary }} data-testid="landing-promo">
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Promozione</div>
                <div className="font-extrabold text-lg">{promotion.name}</div>
                {promotion.descrizione && <p className="text-sm opacity-90 mt-1">{promotion.descrizione}</p>}
                {promotion.condizioni && <p className="text-[11px] opacity-75 mt-2">{promotion.condizioni}</p>}
              </div>
            )}

            <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3" data-testid="booking-form">
              <h2 className="font-bold text-zinc-900">{ev.landing_cta}</h2>
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Nome *" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="bk-nome" />
                <input placeholder="Cognome" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="bk-cognome" />
              </div>
              <input required type="tel" placeholder="Telefono *" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="bk-telefono" />
              <input type="email" placeholder="Email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="bk-email" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 mb-1">Persone</label>
                  <PaxInput value={form.party_size} onChange={(v) => setForm({ ...form, party_size: v })}
                    min={1} max={50} testid="bk-persone" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-500 mb-1">Orario</label>
                  <select required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                    className={inputCls} style={{ borderColor: "#E4E4E7" }} data-testid="bk-ora">
                    <option value="">Scegli…</option>
                    {ev.time_slots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <textarea placeholder="Note (allergie, richieste speciali…)" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                className={`${inputCls} h-auto py-2`} style={{ borderColor: "#E4E4E7" }} data-testid="bk-note" />
              <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="hidden" tabIndex={-1} autoComplete="off" data-testid="bk-website" />
              <label className="flex items-start gap-2 text-xs text-zinc-600">
                <input type="checkbox" checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="w-4 h-4 mt-0.5" data-testid="bk-consenso" />
                Acconsento a ricevere comunicazioni e offerte dal ristorante (facoltativo)
              </label>
              <button disabled={busy} className="w-full h-12 rounded-lg text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: c.primary }} data-testid="bk-submit">
                {busy ? "Invio in corso..." : ev.landing_cta}
              </button>
            </form>
          </>
        )}
        <p className="text-center text-[10px] text-zinc-400 pb-4" data-testid="el-crosspromo">
          Powered by DISHENDA · <a href="/locali" className="underline hover:text-zinc-600 transition-colors">Scopri altri locali nella zona</a>
        </p>
      </main>
    </div>
  );
}

function Centered({ msg, testid }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center text-sm text-zinc-500" data-testid={testid}>
      {msg}
    </div>
  );
}
